import { useEffect, useState } from "react";
import {
  LayoutDashboard, CalendarDays, MessagesSquare, Newspaper,
  Images, FileText, Search, LogOut, Menu, X,
} from "lucide-react";
import { api, formatApiError } from "./api";
import { LOGO_WHITE } from "../data/site";
import DashboardTab from "./tabs/DashboardTab";
import RandevularTab from "./tabs/RandevularTab";
import SohbetlerTab from "./tabs/SohbetlerTab";
import BlogTab from "./tabs/BlogTab";
import GorsellerTab from "./tabs/GorsellerTab";
import IcerikTab from "./tabs/IcerikTab";
import SeoTab from "./tabs/SeoTab";

const TABS = [
  { id: "dashboard", label: "Genel Bakış", icon: LayoutDashboard },
  { id: "randevular", label: "Randevular", icon: CalendarDays },
  { id: "sohbetler", label: "AI Sohbetleri", icon: MessagesSquare },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "gorseller", label: "Görseller", icon: Images },
  { id: "icerik", label: "İçerik", icon: FileText },
  { id: "seo", label: "SEO", icon: Search },
];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("yy_admin_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-950 px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-line-dark bg-ink-900 p-8">
        <div className="flex flex-col items-center gap-4">
          <img src={LOGO_WHITE} alt="Yalı Yurt" className="h-20 w-auto" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-cream-50/50">Yönetim Paneli</p>
        </div>
        <div className="mt-8 space-y-4">
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-cream-50/50">E-posta</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
              className="w-full border border-line-dark bg-ink-950 px-4 py-3 text-sm text-cream-50 outline-none transition-colors focus:border-cream-50/50" placeholder="admin@yaliyurt.com" />
          </div>
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-cream-50/50">Şifre</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
              className="w-full border border-line-dark bg-ink-950 px-4 py-3 text-sm text-cream-50 outline-none transition-colors focus:border-cream-50/50" placeholder="••••••••" />
          </div>
        </div>
        {error && <p data-testid="admin-login-error" className="mt-4 text-xs font-medium text-red-300">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="btn btn-light mt-6 w-full disabled:opacity-60">
          {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
        </button>
        <p className="mt-6 text-center text-xs text-cream-50/60" data-testid="admin-login-developer">
          irvu.io tarafından geliştirildi.
        </p>
      </form>
    </div>
  );
};

const AdminApp = () => {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-ink-950 text-sm text-cream-50/60">Yükleniyor...</div>;
  }
  if (user === false) return <Login onLogin={setUser} />;

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("yy_admin_token");
    setUser(false);
  };

  const Active = {
    dashboard: DashboardTab,
    randevular: RandevularTab,
    sohbetler: SohbetlerTab,
    blog: BlogTab,
    gorseller: GorsellerTab,
    icerik: IcerikTab,
    seo: SeoTab,
  }[tab];

  return (
    <div className="min-h-screen bg-cream-100 lg:grid lg:grid-cols-[240px_1fr]" data-testid="admin-panel">
      <aside className="border-b border-line-dark bg-ink-950 lg:min-h-screen lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_WHITE} alt="Yalı Yurt" className="h-9 w-auto" />
            <span className="text-xs font-extrabold uppercase tracking-[0.18em] text-cream-50">Yönetim</span>
          </div>
          <button className="text-cream-50 lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="admin-menu-toggle" aria-label="Menü">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <nav className={`${menuOpen ? "block" : "hidden"} px-3 pb-4 lg:block`} data-testid="admin-nav">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setMenuOpen(false); }}
              data-testid={`admin-tab-${t.id}`}
              className={`relative flex w-full items-center gap-3 overflow-hidden px-3 py-2.5 text-left text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${
                tab === t.id ? "bg-cream-50 text-ink-900" : "text-cream-50/60 hover:text-cream-50"
              }`}
            >
              <span className={`absolute left-0 top-0 h-full w-[3px] origin-top bg-ink-900 transition-transform duration-300 ${tab === t.id ? "scale-y-100" : "scale-y-0"}`} />
              <t.icon size={15} />
              {t.label}
            </button>
          ))}
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="mt-4 flex w-full items-center gap-3 border-t border-line-dark px-3 py-2.5 pt-4 text-left text-[12px] font-semibold uppercase tracking-[0.12em] text-cream-50/60 transition-colors hover:text-cream-50"
          >
            <LogOut size={15} />
            Çıkış
          </button>
        </nav>
        <p className="border-t border-line-dark px-5 py-4 text-xs text-cream-50/60" data-testid="admin-developer">
          Tasarım & Yazılım: <span className="font-semibold text-cream-50">irvu.io</span>
        </p>
      </aside>
      <main className="p-5 sm:p-8 lg:p-10">
        <Active />
      </main>
    </div>
  );
};

export default AdminApp;
